import { useState } from 'react'
import { supabase } from '../../services/supabaseClient'
import { SECTIONS } from '../../data/sections'
import type { SectionId } from '../../types/tools'
import styles from './AdminPanel.module.css'

interface Notification {
  id: number
  tool_id: string
  message: string
  active: boolean
}

interface NotificationManagerProps {
  notifications: Notification[]
  onRefresh: () => void
}

function getAllTools(): Array<{ id: string; label: string }> {
  const tools: Array<{ id: string; label: string }> = []
  for (const sk of Object.keys(SECTIONS) as SectionId[]) {
    const cats = SECTIONS[sk].cats as unknown as Record<string, { tools: Record<string, { l: string }> }>
    for (const ck of Object.keys(cats)) {
      for (const [tk, tool] of Object.entries(cats[ck].tools)) {
        tools.push({ id: tk, label: tool.l })
      }
    }
  }
  return tools.sort((a, b) => a.label.localeCompare(b.label))
}

export default function NotificationManager({ notifications, onRefresh }: NotificationManagerProps) {
  const [toolId, setToolId] = useState('')
  const [message, setMessage] = useState('')
  const tools = getAllTools()

  const handleCreate = async () => {
    if (!toolId || !message) return
    await supabase
      .from('notifications')
      .insert({ tool_id: toolId, message, active: true })

    setToolId('')
    setMessage('')
    onRefresh()
  }

  const handleToggle = async (id: number, active: boolean) => {
    await supabase
      .from('notifications')
      .update({ active })
      .eq('id', id)
    onRefresh()
  }

  return (
    <div className={styles.card} data-testid="notification-manager">
      <div className={styles.hdrDark}>Notifications</div>

      <div className={styles.notifCreateRow}>
        <select
          className={styles.inlineSelect}
          value={toolId}
          onChange={(e) => setToolId(e.target.value)}
          data-testid="notif-tool-select"
        >
          <option value="">Select tool...</option>
          {tools.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <input
          className={styles.inlineInput}
          style={{ flex: 2 }}
          placeholder="Notification message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="notif-message-input"
        />
        <button className={styles.btnPrimary} onClick={handleCreate} data-testid="notif-create-btn">
          Create
        </button>
      </div>

      {notifications.map((n) => (
        <div className={styles.notifRow} key={n.id} data-testid={`notif-${n.id}`}>
          <span className={n.active ? styles.dotActive : styles.dotInactive} />
          <span className={styles.notifText}>
            <strong>{n.tool_id}</strong>: {n.message}
          </span>
          <button
            className={styles.btnSm}
            onClick={() => handleToggle(n.id, !n.active)}
            data-testid={`notif-toggle-${n.id}`}
          >
            {n.active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ))}
    </div>
  )
}
